import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { priceTablesAdminApi, priceTableKeys } from "@/lib/price-tables";
import { apiFetch } from "@/lib/api";

export interface PriceTablePickerValue {
  priceTableId: number;
  rateAxis: string;
  rateColumn: number | null;
  rateTypeCode: string | null;
}

export interface PriceTablePickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (value: PriceTablePickerValue) => void;
  bomColours?: number | null;
  bomPositions?: number | null;
}

export function PriceTablePickerModal({
  open,
  onOpenChange,
  onConfirm,
  bomColours = null,
  bomPositions = null,
}: PriceTablePickerModalProps) {
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [axis, setAxis] = useState<"Colours" | "Positions" | "Fixed">("Colours");
  const [column, setColumn] = useState<number | "">("");
  const [rateTypeCode, setRateTypeCode] = useState<string>("");

  const list = useQuery({
    queryKey: priceTableKeys.adminList(q, 0),
    queryFn: () => priceTablesAdminApi.listAdmin(q, 0),
  });

  const detail = useQuery({
    queryKey: priceTableKeys.detail(selectedId ?? 0),
    queryFn: () => priceTablesAdminApi.get(selectedId!),
    enabled: selectedId !== null,
  });

  const rateTypes = useQuery({
    queryKey: ["price-table-rate-types", "names"],
    queryFn: async () => {
      const res = await apiFetch<
        { rateType: string }[] | { items: { rateType: string }[] }
      >("/api/lookups/price-table-rate-types?page=0&pageSize=200");
      const list = Array.isArray(res) ? res : res.items;
      return list.map((x) => x.rateType);
    },
  });

  const confirmDisabled = axis === "Fixed" && column === "";

  const activeColumn =
    axis === "Fixed"
      ? (column === "" ? 0 : Number(column))
      : axis === "Positions"
        ? (bomPositions ?? 0)
        : (bomColours ?? 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Choose a price table</DialogTitle>
        </DialogHeader>
        <div className="flex gap-4">
          <div className="flex-1">
            <Input
              aria-label="Search tables"
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <ul className="mt-2 border rounded max-h-60 overflow-auto text-sm">
              {(list.data?.items ?? []).map((t) => (
                <li key={t.priceTableId}>
                  <button
                    type="button"
                    className={`w-full text-left px-2 py-1 ${
                      selectedId === t.priceTableId ? "bg-accent" : ""
                    }`}
                    onClick={() => setSelectedId(t.priceTableId)}
                  >
                    {t.description}{" "}
                    <span className="text-muted-foreground">
                      #{t.priceTableId}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-1 space-y-3">
            {detail.data ? (
              <>
                <div className="text-xs">
                  <div className="font-medium mb-1">Rates preview</div>
                  <table className="border-collapse text-[10px]">
                    <thead>
                      <tr>
                        <th className="border px-1">Range</th>
                        {[1, 2, 3].map((n) => (
                          <th
                            key={n}
                            data-testid={`rate-col-${n}`}
                            className={`border px-1 ${activeColumn === n ? "bg-accent font-semibold" : ""}`}
                          >
                            Rate{n}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.data.rows.map((r) => (
                        <tr key={r.recId}>
                          <td className="border px-1">
                            {r.rangeDesc ?? `${r.rangeFrom}-${r.rangeTo}`}
                          </td>
                          <td className={`border px-1 ${activeColumn === 1 ? "bg-accent" : ""}`}>{r.rate1 ?? ""}</td>
                          <td className={`border px-1 ${activeColumn === 2 ? "bg-accent" : ""}`}>{r.rate2 ?? ""}</td>
                          <td className={`border px-1 ${activeColumn === 3 ? "bg-accent" : ""}`}>{r.rate3 ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-sm">
                  <div className="font-medium">Rate axis</div>
                  {(["Colours", "Positions", "Fixed"] as const).map((a) => (
                    <label key={a} className="mr-3">
                      <input
                        type="radio"
                        aria-label={a}
                        checked={axis === a}
                        onChange={() => setAxis(a)}
                      />{" "}
                      {a}
                    </label>
                  ))}
                  {axis === "Fixed" && (
                    <Input
                      aria-label="Rate column"
                      type="number"
                      className="w-20 mt-1"
                      value={column}
                      onChange={(e) =>
                        setColumn(
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                    />
                  )}
                </div>
                <div className="text-sm">
                  <label htmlFor="pt-rate-type" className="font-medium block">
                    Rate type
                  </label>
                  <select
                    id="pt-rate-type"
                    aria-label="Rate type"
                    className="mt-1 border rounded px-2 py-1 text-sm"
                    value={rateTypeCode}
                    onChange={(e) => setRateTypeCode(e.target.value)}
                  >
                    <option value="">(any)</option>
                    {(rateTypes.data ?? []).map((rt) => (
                      <option key={rt} value={rt}>{rt}</option>
                    ))}
                  </select>
                </div>
                <Button
                  disabled={confirmDisabled}
                  onClick={() =>
                    onConfirm({
                      priceTableId: detail.data.priceTableId,
                      rateAxis: axis,
                      rateColumn:
                        axis === "Fixed" && column !== ""
                          ? Number(column)
                          : null,
                      rateTypeCode: rateTypeCode === "" ? null : rateTypeCode,
                    })
                  }
                >
                  Use this table
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Select a table to preview its rates.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
